import React from 'react';
import { useTranslation } from 'react-i18next';

import { WebModal } from '@components/Modals/WebModal.web';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import { Spinner } from '@components/shared/Spinner';
import theme from '@themes/theme';

interface confirmModalProps {
  showModal: boolean;
  onClose?: () => void;
  onPress: () => void;
  buttonText: string;
  title: string;
  message: string | JSX.Element | React.ReactNode;
  padding?: keyof typeof theme.spacing;
  isAlert?: boolean;
  cancelButtonText?: string;
  isLoading?: boolean;
  confirmButtonBorderWidth?: number;
}

export const ConfirmModal: React.FC<confirmModalProps> = ({
  showModal,
  onClose,
  onPress,
  buttonText,
  title,
  message,
  isAlert = false,
  cancelButtonText = '',
  padding = 's',
  isLoading = false,
  confirmButtonBorderWidth,
}) => {
  const { t } = useTranslation();

  const hasMessage =
    message !== '' && message !== null && message !== undefined;
  let height = undefined;
  if (hasMessage && typeof message === 'string') {
    height = 184;
  }
  // cancelButtonText defaults to '', not undefined; ?? would never fall back to translated text
  const cancelLabel = cancelButtonText ? cancelButtonText : t('shared:cancel'); // NOSONAR

  return (
    <WebModal
      accessibilityLabel={title}
      title={title}
      onClose={onClose}
      closeDisabled={isLoading}
      visible={showModal}
      width={483}
      height={height}>
      <>
        {message !== '' && (
          <>
            {typeof message === 'string' ? (
              <Text
                variant='bodySecondary'
                marginHorizontal='m'
                marginVertical='m'>
                {message}
              </Text>
            ) : (
              <Box marginHorizontal='m' marginVertical='m'>
                {message}
              </Box>
            )}
          </>
        )}
        <Box
          flexDirection='row'
          flex={1}
          alignItems='flex-end'
          paddingHorizontal='m'
          mb='m'
          mt='m'
          alignSelf='flex-end'>
          {!isAlert && (
            <Button
              marginRight='m'
              onPress={onClose}
              borderColor='textPrimary'
              isSmall
              backgroundColor='white'
              disabled={isLoading}>
              <Text
                variant='buttonLabel'
                color={isLoading ? 'grey04' : 'textPrimary'}>
                {cancelLabel}
              </Text>
            </Button>
          )}
          <Button
            onPress={onPress}
            borderColor='textPrimary'
            borderWidth={confirmButtonBorderWidth}
            isSmall
            padding={padding}
            backgroundColor='black'
            disabled={isLoading}>
            <Box
              position='relative'
              width='100%'
              height='100%'
              alignItems='center'>
              {isLoading && (
                <Box
                  position='absolute'
                  alignItems='center'
                  justifyContent='center'
                  top={0}
                  bottom={0}
                  left={0}
                  right={0}>
                  <Spinner
                    size={16}
                    strokeWidth={2}
                    color={theme.colors.white}
                  />
                </Box>
              )}
              <Text
                variant='buttonLabel'
                color='white'
                style={{ opacity: isLoading ? 0 : 1 }}>
                {buttonText}
              </Text>
            </Box>
          </Button>
        </Box>
      </>
    </WebModal>
  );
};
